import { createBrowserRouter, Navigate } from "react-router-dom";
import { RouteError } from "@/components/RouteError";
import { RouteLoading } from "@/components/ui/RouteLoading";
import {
  profileGateLoader,
  publicOnlyLoader,
  requireAuthLoader,
  requireOnboardedLoader,
  verifyEmailLoader,
} from "@/lib/session";
import { lazyPage } from "./lazy-page";
import { ProtectedShell } from "./ProtectedShell";
import { RequireBilling } from "./RequireBilling";
import { RequireConnectPlatform } from "./RequireConnectPlatform";
import { RequireManageAiBot } from "./RequireManageAiBot";
import { RequireManageOrders } from "./RequireManageOrders";
import { RootLayout } from "./RootLayout";

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteError />,
    HydrateFallback: RouteLoading,
    children: [
      { path: "/", lazy: lazyPage(() => import("./pages/LandingPage")) },
      { path: "/landing", element: <Navigate to="/" replace /> },
      {
        path: "/login",
        lazy: lazyPage(() => import("./pages/LoginPage")),
        loader: publicOnlyLoader,
        errorElement: <RouteError />,
      },
      {
        path: "/register",
        lazy: lazyPage(() => import("./pages/RegisterPage")),
        loader: publicOnlyLoader,
        errorElement: <RouteError />,
      },
      {
        path: "/forgot-password",
        lazy: lazyPage(() => import("./pages/ForgotPasswordPage")),
        loader: publicOnlyLoader,
        errorElement: <RouteError />,
      },
      {
        path: "/verify-email",
        lazy: lazyPage(() => import("./pages/VerifyEmailPage")),
        loader: verifyEmailLoader,
        errorElement: <RouteError />,
      },
      {
        path: "/legal/:slug",
        lazy: lazyPage(() => import("./pages/LegalPolicyPage")),
      },
      {
        path: "/invite",
        lazy: lazyPage(() => import("./pages/InvitePage")),
      },

      // Auth required, onboarding NOT required (the profile + consent steps).
      {
        path: "/register/profile",
        lazy: lazyPage(() => import("./pages/register/ProfilePage")),
        loader: profileGateLoader,
        errorElement: <RouteError />,
      },
      {
        path: "/consent",
        lazy: lazyPage(() => import("./pages/ConsentPage")),
        loader: requireAuthLoader,
        errorElement: <RouteError />,
      },

      // App shell: auth + onboarded + consent resolved by the loader.
      {
        element: <ProtectedShell />,
        loader: requireOnboardedLoader,
        errorElement: <RouteError />,
        children: [
          { path: "/dashboard", lazy: lazyPage(() => import("./pages/home/HomePage")) },
          { path: "/chat", lazy: lazyPage(() => import("./pages/chat/ChatPage")) },
          { path: "/crm", lazy: lazyPage(() => import("./pages/crm/CrmPage")) },
          {
            path: "/products",
            lazy: lazyPage(() => import("./pages/products/ProductsPage")),
          },
          {
            path: "/products/new",
            lazy: lazyPage(() => import("./pages/products/NewProductPage")),
          },
          {
            path: "/products/:id",
            lazy: lazyPage(() => import("./pages/products/ProductDetailPage")),
          },
          { path: "/orders", lazy: lazyPage(() => import("./pages/orders/OrdersPage")) },
          {
            path: "/orders/:id",
            lazy: lazyPage(() => import("./pages/orders/OrderDetailPage")),
          },
          { path: "/team", lazy: lazyPage(() => import("./pages/team/TeamPage")) },
          {
            path: "/settings",
            lazy: lazyPage(() => import("./pages/settings/SettingsPage")),
          },
          {
            path: "/settings/callback/facebook",
            lazy: lazyPage(() => import("./pages/settings/callback/FacebookCallbackPage")),
          },
          {
            path: "/settings/callback/instagram",
            lazy: lazyPage(() => import("./pages/settings/callback/InstagramCallbackPage")),
          },
          {
            path: "/settings/callback/tiktok",
            lazy: lazyPage(() => import("./pages/settings/callback/TikTokCallbackPage")),
          },
          {
            element: <RequireConnectPlatform />,
            children: [
              {
                path: "/settings/channels",
                lazy: lazyPage(() => import("./pages/settings/ChannelsPage")),
              },
            ],
          },
          {
            element: <RequireManageAiBot />,
            children: [
              {
                path: "/settings/ai",
                lazy: lazyPage(() => import("./pages/settings/AiSettingsPage")),
              },
            ],
          },
          {
            element: <RequireManageOrders />,
            children: [
              {
                path: "/settings/delivery",
                lazy: lazyPage(() => import("./pages/settings/DeliverySettingsPage")),
              },
            ],
          },
          {
            element: <RequireBilling />,
            children: [
              {
                path: "/billing",
                lazy: lazyPage(() => import("./pages/billing/BillingPage")),
              },
              {
                path: "/billing/orders",
                lazy: lazyPage(() => import("./pages/billing/OrderHistoryPage")),
              },
              {
                path: "/billing/orders/:id",
                lazy: lazyPage(() => import("./pages/billing/OrderPaymentPage")),
              },
              {
                path: "/billing/success",
                lazy: lazyPage(() => import("./pages/billing/BillingSuccessPage")),
              },
              { path: "/billing/return", element: <Navigate to="/billing/orders" replace /> },
            ],
          },
        ],
      },
    ],
  },
]);
